import { ProductListComponentBase } from '@utilities/product-list-component-base';
import { nostoBuildQuery, nostoSearch, nostoTransformToShopifyProducts } from '@utilities/nosto';

/**
 * ProductSlider - Custom element for collection-based product sliders
 *
 * Responsibilities:
 * - Fetch product data from Shopify or Nosto
 * - Render product-card slides inside Embla slider
 * - Replace skeleton placeholders after data load
 * - Manage Embla slider lifecycle and refresh
 *
 * @class
 * @extends ProductListComponentBase
 * @customElement
 */
class ProductSlider extends ProductListComponentBase {
  constructor() {
    super();
    // Embla-specific element references
    this.elements.embla = null;
  }


  /**
   * Initialize ProductSlider-specific setup
   */
  async init() {
    // Parse config and init templates first (needed for showSkeletons)
    this.parseConfiguration();
    this.initTemplates();

    // Show skeletons BEFORE loading data
    this.showSkeletons();
    this.classList.add('product-slider--initialized');

    // Check if we have pre-provided products (e.g., from recommendations)
    // If so, render them directly instead of fetching
    if (this.hasGivenProducts()) {
      this.renderGivenProducts();
      return;
    }

    // Otherwise load data from collection/Nosto APIs
    await this.loadData();
  }

  /**
   * Check if pre-provided products exist in config
   * @returns {boolean}
   */
  hasGivenProducts() {
    return Array.isArray(this.config.givenProducts) && this.config.givenProducts.length > 0;
  }

  /**
   * Render pre-provided products (from recommendations, etc.)
   * Skips API fetching - products are already known
   */
  renderGivenProducts() {
    if (this.allowDebug()) {
      console.log('ProductSlider: Rendering given products', this.config.givenProducts);
    }

    this.setComponentState('results');
    const vendor = this.config.vendor || 'shopify';
    this.renderProducts(this.config.givenProducts, vendor);
  }

  initTemplates() {
    this.elements.container = this.querySelector('.embla__container');
    this.elements.embla = this.querySelector('embla-slider');
    this.templates.skeleton = this.querySelector('template#product-slider-skeleton-template');
    this.templates.productCard = this.querySelector('template#product-slider-product-card-template');
  }

  showSkeletons() {
    if (!this.elements.container || !this.templates.skeleton) return;
    const skeletonClone = this.templates.skeleton.content.cloneNode(true);
    this.elements.container.replaceChildren(skeletonClone);
  }


  async fetchShopifyData() {
    const url = this.buildShopifyUrl();
    const response = await fetch(url, { signal: this.abortController.signal });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const text = await response.text();
    return JSON.parse(text);
  }

  async fetchNostoData() {
    try {
      const defaultSort = 'manual';
      let nostoSortBy = this.config.sortBy || defaultSort;
      let shouldApplySort = true;

      if (nostoSortBy === 'best-selling' || nostoSortBy === 'manual' || nostoSortBy === 'relevance') {
        shouldApplySort = false;
      }

      const queryParams = {
        context: 'collection',
        categoryId: this.config.id,
        type: 'products',
        size: this.config.totalSlides || 12,
        from: 0,
        filters: []
      };

      if (shouldApplySort) {
        queryParams.sortBy = nostoSortBy;
      }

      const queryConfig = nostoBuildQuery(queryParams);
      const response = await nostoSearch(
        queryConfig.query,
        queryConfig.options,
        { debug: this.allowDebug(), trigger: 'product-slider' }
      );

      return nostoTransformToShopifyProducts(response, {
        context: 'collection',
        currentPage: 1,
        pageSize: this.config.totalSlides || 12,
        sortBy: this.config.sortBy || defaultSort,
        generatePaginationParts: () => [],
        getSortOptions: () => []
      });
    } catch (error) {
      throw new Error(`Nosto search failed: ${error.message}`);
    }
  }

  buildShopifyUrl() {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams();
    const productsPerPage = this.config.totalSlides || 12;

    let path = '';
    if (this.config.url) {
      const parsedUrl = new URL(this.config.url, baseUrl);
      path = parsedUrl.pathname;
    } else if (this.config.handle) {
      path = `${window.theme.routes.collections_url}/${this.config.handle}`;
    }

    params.set('view', 'csr-json');
    params.set('products_per_page', productsPerPage);

    return `${baseUrl}${path}?${params.toString()}`;
  }

  /**
   * Set component state for CSS styling
   * @param {string} state
   */
  setComponentState(state) {
    this.dataset.state = state;
    if (this.allowDebug()) {
      console.log('ProductSlider state changed to:', state);
    }
  }

  /**
   * Render product cards in slider format
   * @param {Array} products - Product data array
   * @param {string} vendor - Data source vendor
   */
  renderProducts(products, vendor) {
    const container = this.getContainerElement();
    if (!container || !this.templates.productCard) return;

    const fragment = document.createDocumentFragment();
    const targetCount = this.config.totalSlides || products.length;
    let renderedCount = 0;

    for (const product of products) {
      if (renderedCount >= targetCount) break;
      const productElement = this.createProductCardComponent(product, vendor);
      if (productElement) {
        fragment.appendChild(productElement);
        renderedCount += 1;
      }
    }

    container.replaceChildren(fragment);
    this.refreshEmbla();
  }

  createProductCardComponent(product, vendor) {
    const template = this.templates.productCard;
    if (!template) return null;
    if (!product?.url) {
      if (this.allowDebug()) {
        console.warn('ProductSlider: Skipping product with missing URL', product);
      }
      return null;
    }

    const clone = template.content.cloneNode(true);
    const productCardElement = clone.querySelector('product-card');
    if (!productCardElement) return null;

    const defaultVariantId = product.defaultVariantId || product.default_variant_id || null;
    const productUrl = `${product.url}${defaultVariantId ? `?variant=${defaultVariantId}` : ''}`;
    const fetchUrl = this.buildProductFetchUrl(product, defaultVariantId);

    const sliderId = this.config.sliderId || 'product-slider';
    const componentId = `${sliderId}__product-card-${product.id}`;

    productCardElement.id = componentId;
    productCardElement.dataset.productId = product.id;
    productCardElement.dataset.fetchUrl = fetchUrl;
    productCardElement.dataset.productUrl = productUrl;
    productCardElement.dataset.defaultVariantId = defaultVariantId;
    productCardElement.dataset.productFormId = `quick-add-${sliderId}-${product.id}`;
    productCardElement.dataset.vendor = vendor;

    if (vendor === 'nosto') {
      let nostoContext = 'product-slider-collection';
      if (this.config.context === 'search') {
        nostoContext = 'product-slider-search';
      } else if (this.config.context === 'recommendations') {
        nostoContext = 'product-recommendations';
      }
      productCardElement.dataset.nostoContext = nostoContext;
    }

    return clone;
  }

  buildProductFetchUrl(product, defaultVariantId = null) {
    const url = new URL(product.url, window.location.origin);
    if (defaultVariantId) {
      url.searchParams.set('variant', defaultVariantId);
    }
    url.searchParams.set('view', 'product-json');
    return url.toString();
  }

  /**
   * Get the container element for product cards
   * @returns {HTMLElement} Container element
   */
  getContainerElement() {
    return this.elements.container;
  }

  /**
   * Refresh the Embla slider after content changes
   */
  refreshEmbla() {
    if (!this.elements.embla) return;
    if (typeof this.elements.embla.refresh === 'function') {
      this.elements.embla.refresh();
    } else if (typeof this.elements.embla.init === 'function') {
      this.elements.embla.init();
    }
  }

}

if (!customElements.get('product-slider')) {
  customElements.define('product-slider', ProductSlider);
}
